import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api, ApiErrorPayload} from "../api";
import {
    GameSession,
    QuestionTypeOrder,
    QuestionOrderOption,
    QuestionTypeSelect,
    QuestionTypeConnect, QuestionTextOption, QuestionTypeText, QuestionTypeNumberScale
} from "../../types/gameSession";
import {updateGameTheme} from "./game";
import {RootState} from '../store'
import {FormField} from "../../types/games";

export type GameSessionState = {
    loaded: boolean
    error: string | null
    errorCode: string | null
    session_data?: GameSession
    endFormSuccess: string
    transition: boolean
    showPointsDiff: boolean
    redirectUrl: string | null
};

const initialState: GameSessionState = {
    loaded: false,
    error: null,
    errorCode: null,
    session_data: undefined,
    endFormSuccess: '',
    transition: false,
    showPointsDiff: false,
    redirectUrl: null
};

export type NewSessionPayload = {
    form_fields: FormField[] | undefined
    game_id: number
    language: string
}

export const startGameSession = createAsyncThunk(
    'gameSession/startGameSession',
    async (payload: NewSessionPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post(`/API/games/sessions/`, payload);
            const gameTheme = response.theme;
            if (gameTheme) {
                dispatch(updateGameTheme(gameTheme));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

export type questionAnswerPayload = {
    content_id: number
    answers: any[]
}

export type GameSessionIdPayload = {
    game_session_id: number
};

type ContinueGameSessionPayload = {
    content_id: number
    back: boolean
};
export const continueGameSession = createAsyncThunk(
    'gameSession/continueGameSession',
    async (payload: ContinueGameSessionPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/games/sessions/`, payload);
            const gameTheme = response.theme;
            //const state = (thunkAPI.getState() as RootState).gameSession;
            const currentContentId = payload.content_id;
            if (currentContentId !== response.content_id) {
                dispatch(fadeOutContent());
                setTimeout(() => {
                    dispatch(loadNextContent(response));
                }, 500);
            }
            if (gameTheme) {
                dispatch(updateGameTheme(gameTheme));
            }

            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);


export const pauseGameSession = createAsyncThunk(
    'gameSession/pauseGameSession',
    async (payload: GameSessionIdPayload, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/pause/', payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

type EndFormdata = {
    form_fields: FormField[]
    session_id: number
}

export const submitEndForm = createAsyncThunk(
    'gameSession/submitEndForm',
    async (payload: EndFormdata, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/end/form/', {form: payload});
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

export const sessionIsAlive = createAsyncThunk(
    'gameSession/sessionIsAlive',
    async (payload: GameSessionIdPayload, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/keepalive/', payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

export const endGameSession = createAsyncThunk(
    'gameSession/endGameSession',
    async (_, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/end/', {});
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

export const questionAnswer = createAsyncThunk(
    'gameSession/questionAnswer',
    async (payload: questionAnswerPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/games/sessions/`, payload);
            const gameTheme = response.theme;
            const state = (thunkAPI.getState() as RootState).gameSession;
            const currentContentId = state.session_data?.content_id;
            if (currentContentId !== response.content_id) {
                dispatch(fadeOutContent());
                setTimeout(() => {
                    dispatch(loadNextContent(response));
                }, 500);
            } else {
                dispatch(showExtraContent(response));
            }

            if (gameTheme) {
                dispatch(updateGameTheme(gameTheme));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

const getRejectedError = (payload: unknown, fallback?: string): ApiErrorPayload => {
    if (typeof payload === 'string') {
        return {message: payload};
    }
    if (payload && typeof payload === 'object' && 'message' in payload) {
        const typedPayload = payload as { message?: unknown, code?: unknown };
        if (typeof typedPayload.message === 'string') {
            return {
                message: typedPayload.message,
                code: typeof typedPayload.code === 'string' ? typedPayload.code : undefined
            };
        }
    }
    return {message: fallback || 'Error'};
};

const setRejectedError = (
    state: Pick<GameSessionState, 'error' | 'errorCode'>,
    payload: unknown,
    fallback?: string
) => {
    const error = getRejectedError(payload, fallback);
    state.error = error.message;
    state.errorCode = error.code || null;
};


const slice = createSlice({
    name: 'gameSession',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.errorCode = null;
        },
        setEndFormData(state, action) {
            if (state.session_data?.end?.form) {
                state.session_data.end.form.forEach((field) => {
                    if (field.name in action.payload) {
                        field.value = action.payload[field.name];
                    }
                });
            }
        },
        updateOrder: (state, action: PayloadAction<QuestionOrderOption[]>) => {
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeOrder;
                if (question && question.options) {
                    question.options = action.payload;
                }
            }
        },
        updateOptionSelected: (state, action: PayloadAction<{ optionIndex: number, selected: boolean }>) => {
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeSelect;
                if (question && question.options) {
                    const option = question.options.find(option => option.index === action.payload.optionIndex);
                    if (option) {
                        if (!question.multiple && action.payload.selected) {
                            // If multiple is false and an option is being selected, deselect all other options
                            question.options.forEach(opt => {
                                if (opt !== option) {
                                    opt.selected = false;
                                    opt.answer = '';
                                }
                            });
                        }
                        option.selected = action.payload.selected;
                    }
                }
            }
        },
        updateOptionAnswer: (state, action: PayloadAction<{ optionIndex: number, answer: string, selected: boolean }>) => {
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeSelect;
                if (question && question.options) {
                    const option = question.options.find(option => option.index === action.payload.optionIndex);
                    if (option) {
                        if (!question.multiple) {
                            question.options.forEach(opt => {
                                if (opt !== option) {
                                    opt.selected = false;
                                    opt.answer = '';
                                }
                            });
                        }
                        option.answer = action.payload.answer;
                        option.selected = action.payload.selected;
                    }
                }
            }
        },
        updateConnectSelectedPairs: (state, action: PayloadAction<{ selected_pairs: [number, number][] }>) => {
            const {selected_pairs} = action.payload;
            const question = state.session_data?.question as QuestionTypeConnect;

            if (question) {
                question.selected_pairs = selected_pairs;
            }
        },

        updateText: (state, action: PayloadAction<QuestionTextOption>) => {
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeText;
                if (question && question.options) {
                    // Find the index of the option to be updated
                    const optionIndex = question.options.findIndex(option => option.key === action.payload.key);

                    if (optionIndex !== -1) {
                        // Update the option at the found index
                        question.options[optionIndex]['value'] = action.payload.value;
                    }
                }
            }
        },
        updateNumberScale: (state, action: PayloadAction<{ rating: number }>) => {
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeNumberScale;
                if (question && question.options) {
                    const selectedOption = question.options.find(option => option.rating === action.payload.rating);
                    if (selectedOption) {
                        question.options.forEach(opt => {
                            opt.selected = false;
                        });

                        selectedOption.selected = true;
                    }
                }
            }
        },
        fadeOutContent: (state) => {
            if (state.session_data) {
                state.transition = true;
            }
        },

        showExtraContent: (state, action: PayloadAction<GameSession>) => {
            state.session_data = action.payload;
            state.transition = false;
            state.showPointsDiff = true;
        },
        resetPointsDiff: (state) => {
            state.showPointsDiff = false;
        },
        loadNextContent: (state, action: PayloadAction<GameSession>) => {
            state.session_data = action.payload;
            state.transition = false;
        },
        setGameSession: (state, action: PayloadAction<GameSession>) => {
            state.session_data = action.payload;
            state.loaded = true;
            state.transition = false;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(submitEndForm.pending, (state, action) => {
            state.loaded = true;
            state.error = null;
            state.errorCode = null;
        });
        builder.addCase(submitEndForm.fulfilled, (state, action) => {
            if(action.payload.success_message){
                state.endFormSuccess = action.payload.success_message;
            }
        });
       builder.addCase(submitEndForm.rejected, (state, action) => {
            state.loaded = true;
            setRejectedError(state, action.payload, action.error?.message);
        });
        builder.addCase(continueGameSession.fulfilled, (state, action) => {
            state.loaded = true;
            state.error = null;
            state.errorCode = null;
        });
        builder.addCase(continueGameSession.rejected, (state, action) => {
            state.loaded = true;
            setRejectedError(state, action.payload, action.error?.message);
        });
        builder.addCase(startGameSession.fulfilled, (state, action) => {
            state.session_data = action.payload;
            state.loaded = true;
            state.error = null;
            state.errorCode = null;
        });
        builder.addCase(startGameSession.rejected, (state, action) => {
            state.loaded = true;
            setRejectedError(state, action.payload, action.error?.message);
        });
        builder.addCase(questionAnswer.pending, (state) => {
            state.error = null;
            state.errorCode = null;
        });
        builder.addCase(questionAnswer.fulfilled, (state, action) => {
        });
        builder.addCase(questionAnswer.rejected, (state, action) => {
            state.loaded = true;
            setRejectedError(state, action.payload, action.error?.message);
        });
        builder.addCase(endGameSession.fulfilled, (state, action) => {
            state.redirectUrl = action.payload.redirect;
        });
    }
});

export const {
    fadeOutContent,
    loadNextContent,
    showExtraContent,
    resetPointsDiff,
    updateOptionSelected,
    updateOptionAnswer,
    updateOrder,
    updateConnectSelectedPairs,
    updateText,
    updateNumberScale,
    clearError,
    setGameSession,
    setEndFormData
} = slice.actions;
export default slice.reducer;
